/**
 * How a boundary line is drawn on the maps: a hex colour, a width in pixels
 * and an opacity 0–1. Stored per folder (`ProjectFolder.boundary_style`) and
 * per project (`Project.property_style`), tenant-wide; null means the default
 * look for that kind of boundary.
 */
export interface BoundaryStyle {
  color: string;
  width: number;
  opacity: number;
}

/** A folder's boundary layer: the listing's folder amber. */
export const DEFAULT_FOLDER_BOUNDARY_STYLE: BoundaryStyle = { color: "#f59e0b", width: 2, opacity: 0.85 };
/** A project's property boundaries on the listing map: the project page's green, thinner. */
export const DEFAULT_PROPERTY_BOUNDARY_STYLE: BoundaryStyle = { color: "#388e3c", width: 1.5, opacity: 0.9 };
/** The same green as the project page has always drawn properties in. */
export const DEFAULT_PROJECT_PAGE_PROPERTY_STYLE: BoundaryStyle = { color: "#388e3c", width: 3, opacity: 1 };

export const MIN_BOUNDARY_WIDTH = 0.5;
export const MAX_BOUNDARY_WIDTH = 12;

const HEX = /^#[0-9a-f]{6}$/i;

/** A complete style: the stored one where it is sound, the fallback for the rest. */
export function resolveBoundaryStyle(style: BoundaryStyle | null | undefined, fallback: BoundaryStyle): BoundaryStyle {
  if (!style) return fallback;
  return {
    color: typeof style.color === "string" && HEX.test(style.color) ? style.color.toLowerCase() : fallback.color,
    width: Number.isFinite(style.width) ? style.width : fallback.width,
    opacity: Number.isFinite(style.opacity) ? style.opacity : fallback.opacity,
  };
}

/**
 * `hex` laid over white at `alpha`, as an opaque hex colour. For row tints
 * that must stay opaque (frozen table cells inherit them to cover scrolled
 * content), so rgba() is not an option there.
 */
export function tintOverWhite(hex: string, alpha: number): string {
  const m = HEX.test(hex) ? hex : "#000000";
  const channel = (i: number) => {
    const c = parseInt(m.slice(1 + i * 2, 3 + i * 2), 16);
    return Math.round(255 + (c - 255) * alpha)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(0)}${channel(1)}${channel(2)}`;
}

export function sameBoundaryStyle(a: BoundaryStyle, b: BoundaryStyle): boolean {
  return a.color.toLowerCase() === b.color.toLowerCase() && a.width === b.width && a.opacity === b.opacity;
}
